import { takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { UpdatePhoneNumberModalComponent } from '@auth/components/update-phone-number-modal/update-phone-number-modal.component';
import { AuthService } from '@auth/services/auth/auth.service';
import { EmailAuthService } from '@auth/services/email-auth/email-auth.service';
import { ChangePasswordModalComponent } from '@settings/components/user/change-password-modal/change-password-modal.component';
import { PhoneNumberValidator } from '@shared/classes/phone-number-validator';
import { AlertComponent } from '@shared/components/alerts/alert/alert.component';
import { PhoneNumberControlComponent } from '@shared/components/forms/phone-number-control/phone-number-control.component';
import { StandardControlComponent } from '@shared/components/forms/standard-control/standard-control.component';
import { SwitchControlComponent } from '@shared/components/forms/switch-control/switch-control.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { IMobileNumber } from '@shared/models/numbers';
import { ToastTypes } from '@shared/models/toast';
import { ModalService } from '@shared/services/modal/modal.service';
import { ToastService } from '@shared/services/toast/toast.service';
import { IUser, IUserSettingsForm } from '@user/models/user';
import { UsersService } from '@user/services/users/users.service';

@Component({
	selector: 'app-profile',
	imports: [
		CommonModule,
		FormsModule,
		ReactiveFormsModule,
		AlertComponent,
		LoadingStateComponent,
		StandardControlComponent,
		PhoneNumberControlComponent,
		SwitchControlComponent,
	],
	templateUrl: './profile.component.html',
	styleUrl: './profile.component.scss',
})
export class ProfileComponent implements OnInit, OnDestroy {
	profileForm: FormGroup<IUserSettingsForm>;
	destroyed$ = new EventEmitter<void>();

	currentUser: IUser | null | undefined = null;
	signInProviders: Array<string> = [];

	isLoading = true;
	isSaving = false;

	private usersService = inject(UsersService);
	private toastService = inject(ToastService);
	private authService = inject(AuthService);
	private emailAuthService = inject(EmailAuthService);
	private modalServcice = inject(ModalService);

	get authUser() {
		return this.authService.currentUser;
	}

	ngOnInit(): void {
		this.profileForm = new FormGroup<IUserSettingsForm>({
			displayName: new FormControl<string | null>(null, [Validators.required, Validators.minLength(2), Validators.maxLength(50)]),
			email: new FormControl<string | null>(
				{
					disabled: true,
					value: null,
				},
				[Validators.required, Validators.email, Validators.minLength(2), Validators.maxLength(50)],
			),
			emailVerified: new FormControl<boolean | null>({ value: false, disabled: true }, [Validators.required]),
			phoneNumber: new FormControl<IMobileNumber | null>({ value: null, disabled: true }, [
				Validators.required,
				PhoneNumberValidator.matchDialCode(),
			]),
		});

		this.subscribeToCurrentUser();
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	subscribeToCurrentUser(): void {
		if (!this.authService.currentUser) {
			return;
		}
		this.usersService
			.getDocumentData(this.authService.currentUser.uid)
			.pipe(takeUntil(this.destroyed$))
			.subscribe((user) => {
				this.currentUser = user;
				this.signInProviders = this.authService.currentUser?.providerData.map((provider) => provider.providerId) || [];
				this.isLoading = false;

				this.resetForm();
			});
	}

	resetForm(): void {
		this.profileForm.patchValue({
			displayName: this.currentUser?.displayName,
			email: this.currentUser?.email,
			emailVerified: this.authUser?.emailVerified,
			phoneNumber: this.currentUser?.phoneNumber,
		});
	}

	handleUpdatePassword(): void {
		this.modalServcice.open(ChangePasswordModalComponent, false, 'lg');
	}

	handleUpdatePhoneNumber(): void {
		const modalRef = this.modalServcice.open(UpdatePhoneNumberModalComponent, false, 'lg');
		const componentInstance = modalRef.componentInstance as UpdatePhoneNumberModalComponent;
		componentInstance.currentPhoneNumber = this.currentUser?.phoneNumber || null;
	}

	handleSendEmailVerification(): void {
		this.emailAuthService.sendEmailVerification();
	}

	async handleSave(): Promise<void> {
		if (this.profileForm.invalid) {
			return;
		}

		this.isSaving = true;

		try {
			await this.authService.updateUserProfile(this.profileForm.value.displayName!);

			const updateData: Partial<IUser> = {
				displayName: this.profileForm.value.displayName!,
				updatedAt: Timestamp.now(),
			};

			await this.usersService.update(this.currentUser!.uid, updateData);

			this.toastService.showToast({
				title: 'Profile Updated',
				message: 'Your profile has been updated successfully.',
				type: ToastTypes.SUCCESS,
			});

			this.isSaving = false;
			this.profileForm.markAsPristine();
			this.profileForm.markAsUntouched();
		} catch (error) {
			this.isSaving = false;
		}
	}

	handleCancel(): void {
		this.resetForm();
		this.profileForm.markAsPristine();
		this.profileForm.markAsUntouched();
	}
}
